import {
	DropDownButton,
	HTMLInputElementExtended,
	HTMLTextAreaElementExtended,
	StylovyzeTable,
	UnitDisplay,
	UnitField,
	CurrencyField,
	useSettings,
	useUnits,
	getCurrency,
} from '@innovyze/stylovyze';
import React, { useRef, FocusEvent } from 'react';
import { Warning } from '@mui/icons-material';
import { Grid, TextField } from '@mui/material';
import ScoreButton from '../ScoreButton/ScoreButton.component';
import { ScoreTableRow } from '@Types/scoreTableType';
import {
	selectRiskEditScores,
	selectRiskNullScore,
	selectRiskScores,
	selectRiskUnitScores,
} from '@Selectors';
import {
	setRiskEditScores,
	setRiskNullScore,
	setRiskScores,
	setRiskScoreTable,
	setRiskUnitScores,
} from '@Actions';
import { useDispatch } from 'react-redux';
import { useGlobalization } from '@Translations/useGlobalization';

interface ScoreTableProps {
	isLoading: boolean;
	isUnique: boolean;
	isNew: boolean;
	scoreRows: ScoreTableRow[];
	rangeClasses: number;
	setRangeClasses: React.Dispatch<React.SetStateAction<number>>;
	hasError: boolean;
	precision?: number;
	unit?: string;
	currency?: string;
}

const ScoreTable = ({
	isLoading,
	isUnique,
	scoreRows,
	rangeClasses,
	setRangeClasses,
	hasError,
	precision,
	unit,
	currency,
}: ScoreTableProps) => {
	const { t } = useGlobalization();
	const dispatch = useDispatch();
	const ScoreRef = useRef<HTMLDivElement>(null);
	const { companySettings } = useSettings();

	const { getSystemUnit, getStandardisedValue } = useUnits();

	const headers = [
		{
			cell: (
				<Grid
					ref={ScoreRef}
					container
					item
					style={{ width: '100px', alignContent: 'center' }}>
					{t('Score')}
					{hasError ? (
						<Warning
							color="error"
							style={{
								marginLeft: '10px',
							}}
						/>
					) : null}
				</Grid>
			),
			dataCy: 'col-Score',
			key: 'Score',
			width: '150px',
		},
		{
			cell: unit && unit.length != 0 ? t('Range') : t('Value'),
			dataCy: 'col-Range',
			key: 'Range',
			width: '120px',
		},
		{
			cell: '',
			dataCy: 'col-Value',
			key: 'Value',
		},
	];

	const scoreOptions = [
		{ name: '0' },
		{ name: '1' },
		{ name: '2' },
		{ name: '3' },
		{ name: '4' },
		{ name: '5' },
		{ name: '6' },
		{ name: '7' },
		{ name: '8' },
		{ name: '9' },
		{ name: '10' },
	];

	const riskScore = selectRiskScores();
	const riskEditScore = selectRiskEditScores();
	const riskUnitScore = selectRiskUnitScores();
	const riskNullScore = selectRiskNullScore();

	const updateRiskScore = (
		index: number,
		value: number,
		valueUnit?: number,
	) => {
		let up: boolean | undefined = undefined;
		const decimalPt = precision !== undefined ? precision : 1;
		const precisionPowered = Math.pow(10, decimalPt);
		const minDiff =
			Math.round((1 / precisionPowered) * precisionPowered) /
			precisionPowered;

		let newScore = [...riskScore];
		newScore[index] = value;

		// If we adjusting the min or max make sure we have enough space to make a valid range
		if (value === newScore[newScore.length - 1]) {
			if (index === 0) {
				// The last two elements should match
				newScore[newScore.length - 1] =
					newScore[0] + minDiff * (newScore.length - 2);
				newScore[newScore.length - 2] =
					newScore[0] + minDiff * (newScore.length - 2);
				up = false;
			} else if (index === newScore.length - 1) {
				newScore[0] =
					newScore[newScore.length - 1] -
					minDiff * (newScore.length - 2);
				up = true;
			}
		}

		if (up === undefined) {
			// Check if it overlaps the bounds of it's neighbours
			if (index >= 1 && value <= newScore[index - 1]) up = false;
			if (value >= newScore[index + 1]) up = true;
		}

		// We need to work out if we need do this next bit in native or system units
		if (riskUnitScore && valueUnit && valueUnit !== value) {
			newScore = [...riskUnitScore];
			newScore[index] = valueUnit;
			// Since we can edit the upper value we need to push the > max out as required
			if (index === newScore.length - 2) newScore[index + 1] = valueUnit;
		} else {
			// Since we can edit the upper value we need to push the > max out as required
			if (index === newScore.length - 2) {
				// We need to double check that the values can actually fit with the minDiff
				if (value < newScore[0] + minDiff * (newScore.length - 2)) {
					value = newScore[0] + minDiff * (newScore.length - 2);
					newScore[index] = value;
				}

				// Set the upper bounds
				newScore[index + 1] = value;
			}
		}

		// Do we need to verify the scores and in which direction
		if (up != undefined) {
			if (up) {
				let last = newScore[0] + minDiff;
				for (let i = 1; i < newScore.length - 2; i++) {
					if (newScore[i] <= last) newScore[i] = last;
					last = newScore[i] + minDiff;
					last =
						Math.round(last * precisionPowered) / precisionPowered;
				}
			} else {
				let last = newScore[newScore.length - 2] - minDiff;
				for (let i = newScore.length - 3; i > 0; i--) {
					if (newScore[i] >= last) newScore[i] = last;
					last = newScore[i] - minDiff;
					last =
						Math.round(last * precisionPowered) / precisionPowered;
				}
			}

			// Now verify the extents
			if (newScore[1] <= newScore[0]) {
				let last = newScore[0] + minDiff;
				for (let i = 1; i < newScore.length - 2; i++) {
					if (newScore[i] <= last) newScore[i] = last;
					last = newScore[i] + minDiff;
					last =
						Math.round(last * precisionPowered) / precisionPowered;
				}
			} else if (
				newScore[newScore.length - 3] >= newScore[newScore.length - 2]
			) {
				let last = newScore[newScore.length - 2] - minDiff;
				for (let i = newScore.length - 3; i > 0; i--) {
					if (newScore[i] >= last) newScore[i] = last;
					last = newScore[i] - minDiff;
					last =
						Math.round(last * precisionPowered) / precisionPowered;
				}
			}
		}

		// Are we running a different display unit?
		if (
			riskUnitScore &&
			unit &&
			valueUnit != undefined &&
			valueUnit !== value
		) {
			const internalUnit = getSystemUnit(unit);
			let adjustment = 1;
			if (valueUnit !== undefined && value != valueUnit) {
				// mm -> in -> cm
				if (unit === 'mm') adjustment = 10;
			}

			const nativeScores = newScore.map(score => {
				return (
					parseFloat(
						getStandardisedValue(`${score} ${internalUnit}`),
					) * adjustment
				);
			});
			const stringScores = nativeScores.map(score => {
				return score.toString();
			});
			dispatch(setRiskEditScores(stringScores));
			dispatch(setRiskScores(nativeScores));
			dispatch(setRiskUnitScores(newScore));

			// Now switch back to the native scores for the next bit as thats in native units
			newScore = nativeScores;
		} else {
			const stringScores = newScore.map(score => {
				return score.toString();
			});
			dispatch(setRiskEditScores(stringScores));
			dispatch(setRiskScores(newScore));
		}

		// Finally update the score rows that will be saved
		const newScoreRows: ScoreTableRow[] = [];
		for (let i = 0; i < newScore.length; i++) {
			if (i == 0) {
				newScoreRows.push({
					...scoreRows[0],
					Value: `< ${newScore[0]}`,
				});
			} else if (i < newScore.length - 1) {
				newScoreRows.push({
					...scoreRows[i],
					Value: `${newScore[i - 1]} <= ${newScore[i]}`,
				});
			} else {
				newScoreRows.push({
					...scoreRows[i],
					Value: `> ${newScore[i]}`,
				});
			}
		}
		dispatch(setRiskScoreTable(newScoreRows));
	};

	const scoreField = (index: number) => {
		if (unit && unit.length != 0) {
			return (
				<UnitField
					data-cy={`score-${index}`}
					style={{ width: '200px' }}
					unit={unit}
					defaultValue={riskEditScore[index]}
					step={
						precision === undefined || precision === 0
							? '1'
							: (1.0 / (precision * 10)).toString()
					}
					onChange={e => {
						let value = parseFloat(e.target.value);
						const valueUnit = e.target.originalValue
							? parseFloat(e.target.originalValue)
							: undefined;

						// Some imperial conversions dont come back correctely and need an adjustment
						if (valueUnit !== undefined && value != valueUnit) {
							// mm -> in -> cm
							if (unit === 'mm') value *= 10;
						}

						if (!isNaN(value)) {
							const stringScores = riskScore.map(score => {
								return score.toString();
							});
							stringScores[index] = value.toString();
							dispatch(setRiskEditScores(stringScores));
						}
					}}
					onBlur={(
						e: FocusEvent<
							| HTMLInputElementExtended
							| HTMLTextAreaElementExtended
						>,
					) => {
						let value = parseFloat(e.target.value);
						const valueUnit = e.target.originalValue
							? parseFloat(e.target.originalValue)
							: undefined;

						// Some imperial conversions dont come back correctely and need an adjustment
						if (valueUnit !== undefined && value != valueUnit) {
							// mm -> in -> cm
							if (unit === 'mm') value *= 10;
						}

						if (!isNaN(value)) {
							updateRiskScore(index, value, valueUnit);
						}
					}}
				/>
			);
		} else if (currency) {
			return (
				<CurrencyField
					data-cy={`score-${index}`}
					style={{ width: '200px' }}
					// For the moment just use the default system currency... this MIGHT be used later
					// currency={currency}
					defaultValue={riskScore[index].toString()}
					// eslint-disable-next-line @typescript-eslint/no-empty-function
					onChange={() => {}}
					onBlur={(e: { target: { value: string } }) => {
						const value = parseFloat(e.target.value);
						if (!isNaN(value)) {
							updateRiskScore(index, value);
						}
					}}
					step={
						precision === undefined || precision === 0
							? '1'
							: (1.0 / (precision * 10)).toString()
					}
				/>
			);
		} else {
			return (
				<TextField
					data-cy={`score-${index}`}
					style={{ width: '200px' }}
					value={riskEditScore[index]}
					onChange={(e: { target: { value: string } }) => {
						let value = parseFloat(e.target.value);

						if (precision !== undefined)
							value = parseFloat(value.toFixed(precision));

						if (!isNaN(value)) {
							const stringScores = riskScore.map(score => {
								return score.toString();
							});
							stringScores[index] = value.toString();
							dispatch(setRiskEditScores(stringScores));
						} else {
							const newEditScore = [...riskEditScore];
							newEditScore[index] = '';
							dispatch(setRiskEditScores(newEditScore));
						}
					}}
					onBlur={(e: { target: { value: string } }) => {
						let value = parseFloat(e.target.value);

						if (precision !== undefined)
							value = parseFloat(value.toFixed(precision));

						if (!isNaN(value)) {
							updateRiskScore(index, value);
						} else {
							const stringScores = riskScore.map(score => {
								return score.toString();
							});
							dispatch(setRiskEditScores(stringScores));
						}
					}}
					inputProps={{
						step:
							precision === undefined || precision === 0
								? '1'
								: 1.0 / (precision * 10),
					}}
					variant="outlined"
					type="number"
					size="small"
				/>
			);
		}
	};

	const rangeField = (index: number) => {
		let minDiff = 0;
		const decimalPt = precision !== undefined ? precision : 1;
		const precisionPowered = Math.pow(10, decimalPt);

		// For the first item... ignore the min diff as that comparison is less than
		if (index > 1) {
			minDiff =
				Math.round((1 / precisionPowered) * precisionPowered) /
				precisionPowered;

			// Are we running a different display unit?
			if (riskUnitScore && unit) {
				const internalUnit = getSystemUnit(unit);
				if (internalUnit !== unit) {
					let adjustment = 1;
					// mm -> in -> cm
					if (unit === 'mm') adjustment = 10;

					minDiff =
						parseFloat(
							getStandardisedValue(`${minDiff} ${internalUnit}`),
						) * adjustment;
				}
			}
		}

		// Do this to avoid any decimal place issues when displaying
		const value =
			Math.round((riskScore[index - 1] + minDiff) * precisionPowered) /
			precisionPowered;

		if (unit && unit.length != 0) {
			return (
				<Grid container wrap="nowrap">
					<Grid item style={{ alignSelf: 'center' }}>
						<UnitDisplay
							cy={`prevscore-${index}`}
							value={`${riskScore[index - 1] + minDiff} ${unit}`}
						/>
					</Grid>
					<Grid
						item
						style={{ alignSelf: 'center', padding: '0px 10px' }}>
						-
					</Grid>
				</Grid>
			);
		} else if (currency) {
			return (
				<Grid container wrap="nowrap">
					<Grid item style={{ alignSelf: 'center' }}>
						{getCurrency(value, companySettings)}
					</Grid>
					<Grid
						item
						style={{ alignSelf: 'center', padding: '0px 10px' }}>
						-
					</Grid>
				</Grid>
			);
		} else {
			return (
				<Grid container wrap="nowrap">
					<Grid item style={{ alignSelf: 'center' }}>
						{value}
					</Grid>
					<Grid
						item
						style={{ alignSelf: 'center', padding: '0px 10px' }}>
						-
					</Grid>
				</Grid>
			);
		}
	};

	const scoreDisplayField = (index: number) => {
		if (unit && unit.length != 0) {
			return (
				<UnitDisplay
					cy={`score-${index}`}
					value={`${riskScore[index]} ${unit}`}
				/>
			);
		} else if (currency) {
			return getCurrency(riskScore[index], companySettings);
		} else {
			return riskScore[index];
		}
	};

	const getScoreRowsTableRows = () => {
		const scoreRowsTableRows = scoreRows.map((row, index) => {
			let value: string | React.ReactNode = row.Value;
			let range;
			if (!isUnique && riskScore) {
				switch (index) {
					case 0:
						value = scoreField(index);
						range = '<';
						break;
					case riskScore.length - 1:
						value = scoreDisplayField(index);
						range = '>';
						break;
					default:
						value = scoreField(index);
						range = rangeField(index);
						break;
				}
			}

			return {
				cells: [
					<div key={row.ID} role="button">
						<ScoreButton
							options={scoreOptions}
							initialSelectedIndex={
								row.Score >= 0 && row.Score <= 10
									? row.Score
									: -1
							}
							onSelectChanged={selectedScore => {
								const newTable = scoreRows.map(
									(row, eachIndex) =>
										eachIndex == index
											? {
													...row,
													Score: +selectedScore,
											  }
											: row,
								);

								dispatch(setRiskScoreTable(newTable));
								if (row.Value == '--')
									dispatch(setRiskNullScore(+selectedScore));
							}}></ScoreButton>
					</div>,
					range,
					value,
				],
				dataCy: 'row' + row.ID,
				key: row.ID,
			};
		});

		if (!isUnique) {
			scoreRowsTableRows.push({
				cells: [
					<div key={scoreRows.length.toString()} role="button">
						<ScoreButton
							options={scoreOptions}
							initialSelectedIndex={
								riskNullScore != undefined &&
								riskNullScore >= 0 &&
								riskNullScore <= 10
									? riskNullScore
									: -1
							}
							onSelectChanged={selectedScore => {
								dispatch(setRiskNullScore(+selectedScore));
							}}></ScoreButton>
					</div>,
					'--',
					'',
				],
				dataCy: 'row' + scoreRows.length.toString(),
				key: scoreRows.length.toString(),
			});
		}

		return scoreRowsTableRows;
	};

	const classOptions = () => {
		const options = [];
		for (let i = 1; i <= 10; ++i) {
			options.push({
				onClick: () => {
					setRangeClasses(i);
				},
				name: t('{{classes}} Classes', { classes: i + 2 }),
			});
		}
		return options;
	};

	const getTitleArea = () => (
		<DropDownButton
			options={classOptions()}
			color="primary"
			variant="outlined"
			popupZIndex={1300}
			initialSelectedIndex={rangeClasses - 1}
			width={150}
		/>
	);
	return (
		<StylovyzeTable
			className="ScrollTable"
			dataCy="score-table"
			headers={headers}
			title={t('Set Score')}
			titleArea={!isLoading && !isUnique ? getTitleArea() : null}
			rows={getScoreRowsTableRows()}
			initialized={!isLoading}
		/>
	);
};

export default ScoreTable;
